/**
 * Позиция ЦЕЛИ команды — аккаунта или когорты — и её продажа по аккаунтам.
 *
 * Модуль ЧИСТЫЙ по правилу lib/*.ts: здесь решается, СКОЛЬКО share и С КАКОГО
 * кошелька уйдёт на биржу при нажатии «продать», и это обязано проверяться
 * тестом.
 *
 * # Почему продажа раскладывается по аккаунтам
 *
 * Позиция живёт на кошельке аккаунта, и продать share можно только оттуда, где
 * они лежат. Живой стенд multi 2026-10-07: выбран A01, DOWN куплен только A02.
 * Снимок A01 по DOWN честно пуст, а журнал филлов складывал сделки ВСЕХ
 * аккаунтов — и «100% по журналу» ушло продажей 10.61 DOWN от A01, получив
 * «not enough balance: balance 0». Поэтому:
 *
 *   - позиция и журнал берутся только у аккаунтов цели (scopeFills);
 *   - продажа — это ноги, по одной на аккаунт цели, и каждая продаёт долю
 *     СВОЕЙ позиции (sellPlan). У когорты это и есть «закрыть когорту»: режим
 *     each из Д-5 («каждый ставит полный размер») для продажи означал бы
 *     продать с каждого кошелька одно и то же число, которого на большинстве
 *     из них нет.
 */

import { add, div, format, mul, parse, type Micro } from "./money";
import { dust, fractionProblem, positionOf, sellable, sizeForFraction } from "./position";
import type { SellFraction } from "./position";
import type { Order, Position, PositionsResponse, Trade } from "../api/types";

/**
 * Наши сделки аккаунтов цели.
 *
 * Сделка без аккаунта (строка до бэкфилла) не достаётся никому: приписать её
 * каждому значило бы удвоить позицию когорты, а угадать владельца нечем.
 */
export function scopeFills(trades: readonly Trade[], accounts: readonly string[]): Trade[] {
  if (accounts.length === 0) return [];
  const want = new Set(accounts);
  return trades.filter((t) => t.account !== undefined && want.has(t.account));
}

/**
 * Снимки аккаунтов цели — одним снимком.
 *
 * Размер — сумма; средняя — взвешенная по размеру; `known` — только когда
 * известны ВСЕ: сумма по части кошельков выглядела бы позицией когорты, а
 * была бы позицией её половины. Возраст — самый старый: на экране он
 * предупреждает, и предупреждать надо по худшему.
 *
 * null — снимка нет ни у одного аккаунта (ещё грузятся или цели нет).
 */
export function mergeSnapshots(
  snapshots: readonly (PositionsResponse | undefined)[],
): PositionsResponse | null {
  const got = snapshots.filter((s): s is PositionsResponse => s !== undefined);
  if (got.length === 0) return null;
  if (got.length === 1 && snapshots.length === 1) return got[0] ?? null;

  const byAsset = new Map<string, { pos: Position; size: Micro; cost: Micro; chain: boolean }>();
  for (const snap of got) {
    for (const p of snap.positions) {
      const size = parse(p.size) ?? 0;
      const cost = mul(parse(p.avg_price) ?? 0, size) ?? 0;
      const cur = byAsset.get(p.asset_id);
      if (!cur) {
        byAsset.set(p.asset_id, { pos: p, size, cost, chain: p.source === "chain" });
        continue;
      }
      cur.size = add(cur.size, size) ?? cur.size;
      cur.cost = add(cur.cost, cost) ?? cur.cost;
      cur.chain = cur.chain && p.source === "chain";
      cur.pos = { ...cur.pos, redeemable: cur.pos.redeemable || p.redeemable };
    }
  }

  const positions: Position[] = [];
  for (const { pos, size, cost, chain } of byAsset.values()) {
    const avg = size > 0 ? (div(cost, size) ?? 0) : 0;
    positions.push({
      ...pos,
      size: format(size),
      avg_price: format(avg),
      source: chain ? "chain" : "indexer",
    });
  }

  const asOf = got
    .map((s) => s.as_of)
    .filter((t): t is string => Boolean(t))
    .sort()[0];
  return {
    positions,
    known: got.length === snapshots.length && got.every((s) => s.known),
    stale: got.some((s) => s.stale),
    chain_known: got.every((s) => s.chain_known === true),
    ...(asOf ? { as_of: asOf } : {}),
  };
}

/** Позиция одного аккаунта по токену — всё, из чего считается его нога продажи. */
export interface AccountHolding {
  /** Funder, нижний регистр. */
  account: string;
  /**
   * Снимок аккаунта по токену, share; null — снимок неизвестен. Пустой
   * известный снимок — "0", а не null: «нет» и «не знаю» различаются.
   */
  indexed: string | null;
  /** Позиция по журналу ЭТОГО аккаунта, share; null — филлов не было. */
  filled: string | null;
  /** Журнал после недавней продажи (position.journalAfterSale); null — снимку верить. */
  afterSale?: string | null;
}

/** Снимок аккаунта по токену в форме AccountHolding.indexed. */
export function indexedOf(snapshot: PositionsResponse | undefined, assetId: string): string | null {
  if (!snapshot?.known) return null;
  return positionOf(snapshot.positions, assetId)?.size ?? "0";
}

/** Нога продажи: с какого аккаунта и сколько. */
export interface SellLeg {
  account: string;
  shares: string;
}

export interface SellPlan {
  /** Ноги в порядке аккаунтов цели. Пусто — продавать нечего или нельзя. */
  legs: SellLeg[];
  /** Сумма ног — то, что уйдёт на биржу; null — ног нет. */
  size: string | null;
  /**
   * Доступно к продаже по цели целиком; null — позиция неизвестна у всех.
   * "0" — известно, что продавать нечего: кнопки продажи не рисуются.
   */
  shares: string | null;
  /** Стоит в стакане нашими продажами аккаунтов цели. Уже вычтено. */
  reserved: string;
  /** "fills" — хоть одна нога посчитана по журналу, а не по снимку. */
  basis: "snapshot" | "fills" | null;
  /** Непродаваемые хвосты ног (мельче шага размера), сложенные. */
  tail: string;
  /**
   * Почему не продаётся: ног нет — причина целиком; ноги есть — кого из
   * аккаунтов цели они не охватят. null — продаётся всё, что есть.
   */
  problem: string | null;
}

/**
 * План продажи доли `fraction` по аккаунтам цели.
 *
 * Каждый аккаунт продаёт долю СВОЕЙ доступной позиции (lib/position.sellable
 * со своим резервом), округлённую вниз до шага. Аккаунт, чья доля мельче
 * минимума рынка, пропускается и называется: поднять его долю до минимума
 * значило бы продать больше, чем просили, — та же причина, по которой
 * fractionProblem отвергает, а не округляет вверх.
 *
 * Пропуск одного не гасит остальных: закрыть когорту, кроме кошелька с хвостом
 * в 3 share, полезнее, чем не закрыть ничего, — но пропуск обязан быть виден.
 */
export function sellPlan(input: {
  holdings: readonly AccountHolding[];
  orders: Record<string, Order>;
  assetId: string;
  fraction: SellFraction;
  minOrderSize: string;
  /** Подпись аккаунта для причин: слот, а не адрес. */
  label: (account: string) => string;
  /** Почему позиция неизвестна — для причины, когда неизвестна у всех. */
  unknownReason?: string | null;
}): SellPlan {
  const { holdings, orders, assetId, fraction } = input;
  const many = holdings.length > 1;
  const legs: SellLeg[] = [];
  const skipped: string[] = [];
  let total: Micro = 0;
  let available: Micro = 0;
  let reserved: Micro = 0;
  let tail: Micro = 0;
  let anyKnown = false;
  let byFills = false;
  let lastProblem: string | null = null;

  for (const h of holdings) {
    const avail = sellable({
      indexed: h.indexed,
      filled: h.filled,
      afterSale: h.afterSale ?? null,
      orders,
      assetId,
      account: h.account,
    });
    reserved = add(reserved, parse(avail.reserved) ?? 0) ?? reserved;
    if (avail.shares === null) {
      if (many) skipped.push(`${input.label(h.account)}: позиция неизвестна`);
      continue;
    }
    anyKnown = true;
    const have = parse(avail.shares) ?? 0;
    if (have <= 0) continue;
    available = add(available, have) ?? available;
    tail = add(tail, parse(dust(avail.shares)) ?? 0) ?? tail;

    const problem = fractionProblem(avail.shares, fraction, input.minOrderSize);
    const size = sizeForFraction(avail.shares, fraction);
    if (problem !== null || size === null) {
      const why = problem ?? "нечего продавать";
      lastProblem = why;
      skipped.push(many ? `${input.label(h.account)}: ${why}` : why);
      continue;
    }
    legs.push({ account: h.account, shares: size });
    total = add(total, parse(size) ?? 0) ?? total;
    if (avail.basis === "fills") byFills = true;
  }

  const shares = anyKnown ? format(available) : null;
  let problem: string | null;
  if (legs.length > 0) {
    problem = skipped.length > 0 ? `не продаст: ${skipped.join("; ")}` : null;
  } else if (!anyKnown) {
    problem = input.unknownReason ?? (skipped.length > 0 ? skipped.join("; ") : "позиция неизвестна");
  } else if (skipped.length === 0) {
    problem = null; // известно, что продавать нечего
  } else {
    problem = many ? skipped.join("; ") : lastProblem;
  }

  return {
    legs,
    size: legs.length > 0 ? format(total) : null,
    shares,
    reserved: format(reserved),
    basis: legs.length === 0 ? (anyKnown ? "snapshot" : null) : byFills ? "fills" : "snapshot",
    tail: tail > 0 ? format(tail) : "",
    problem,
  };
}
